import React from 'react';
import type { GridCell, GridRow, Milestone } from '../../types/life';
import { cn } from '../../utils/cn';
import { plural } from '../../utils/format';
import { DAY_COLUMNS } from '../../utils/lifeGrid';
import type { RemainingCounter } from '../../utils/counters';
import { StrikeRow } from '../StrikeRow';
import { RemainingCounters } from '../RemainingCounters';
interface NowViewProps {
  stripRows: GridRow[];
  stripColumns: number;
  dayRows: GridRow[];
  year: number;
  now: Date;
  compact: boolean;
  counters: RemainingCounter[];
  milestoneByCell: Map<string, Milestone>;
  focusedKey: string | null;
  onSelectYear: (cell: GridCell) => void;
  onSelectMonth: (row: GridRow) => void;
  onSelectDay: (cell: GridCell) => void;
}
export function NowView({
  stripRows,
  stripColumns,
  dayRows,
  year,
  now,
  compact,
  counters,
  milestoneByCell,
  focusedKey,
  onSelectYear,
  onSelectMonth,
  onSelectDay
}: NowViewProps) {
  const daysLeftInYear = Math.max(0, Math.ceil((new Date(year + 1, 0, 1).getTime() - now.getTime()) / 86_400_000));
  return <div className={compact ? 'space-y-8' : 'space-y-12'} data-id="element-177" data-mp="1oqkzaoh34ai2">
      <section aria-label="Years of your life" className={compact ? 'space-y-3' : 'space-y-4'} data-id="element-178" data-mp="1tq77gji230k0">
        {stripRows.map(row => <StrikeRow key={row.key} columns={stripColumns} gapPx={compact ? 8 : 12} labelWidth={0} fraction={row.fraction} data-id="element-179" data-mp="1fu25la114ikf">
            {row.cells.map(cell => {
          if (cell.state === 'void') return <span key={cell.key} aria-hidden data-id="element-180" data-mp="1au7fv9szpz0y" />;
          const milestone = milestoneByCell.get(cell.key);
          const focused = focusedKey === cell.key;
          return <button key={cell.key} type="button" data-cell={cell.key} title={milestone?.title} onClick={() => onSelectYear(cell)} aria-label={milestone ? `${cell.label}, milestone: ${milestone.title}` : cell.label} className={cn('relative px-0.5 py-1 font-semibold tracking-tight transition-colors duration-150 ease-swift focus-visible:outline focus-visible:outline-1 focus-visible:outline-accent', compact ? 'text-center text-sm' : 'text-left text-base md:text-lg', cell.state === 'past' && 'text-ink', cell.state === 'future' && 'text-muted hover:text-ink', cell.state === 'current' && 'text-accent', milestone && 'text-accent', focused && 'bg-accent-soft')} data-id="element-181" data-mp="113mljykcngnu">
                  {cell.label}
                  {(milestone || cell.state === 'current') && <span className="bg-accent absolute -right-1 top-0.5 h-1.5 w-1.5 rounded-full" aria-hidden data-id="element-182" data-mp="1sautm4a5aw1y" />}
                </button>;
        })}
          </StrikeRow>)}
      </section>

      <section aria-label={`Current year: ${year}`} className="space-y-3" data-id="element-183" data-mp="1d8mlugw0tsty">
        <div className="border-line flex items-baseline justify-between gap-4 border-b pb-2" data-id="element-184" data-mp="1i5lh52rf85tq">
          <h2 className="text-ink font-mono text-sm" data-id="element-185" data-mp="1n4y55qu13ixw">{year}</h2>
          <p className="text-muted text-xs" data-id="element-186" data-mp="1n3mz1kd41z3m">
            <span className="text-accent font-mono tabular-nums" data-id="element-187" data-mp="1rx9e1j6qyf2l">{daysLeftInYear}</span>{' '}
            {plural(daysLeftInYear, 'day')} left
          </p>
        </div>

        <div className={compact ? 'space-y-1.5' : 'space-y-2'} data-id="element-188" data-mp="20wmczhx8vl92">
          {dayRows.map(row => {
          const monthMilestone = milestoneByCell.get(row.key);
          const monthFocused = focusedKey === row.key;
          return <StrikeRow key={row.key} columns={DAY_COLUMNS} gapPx={compact ? 3 : 6} labelWidth={compact ? 34 : 52} fraction={row.fraction} thickness={1.5} label={<button type="button" data-cell={row.key} title={monthMilestone?.title} onClick={() => onSelectMonth(row)} aria-label={monthMilestone ? `${row.label} ${year}, milestone: ${monthMilestone.title}` : `${row.label} ${year}`} className={cn('relative text-left font-semibold leading-none transition-colors duration-150 ease-swift focus-visible:outline focus-visible:outline-1 focus-visible:outline-accent', compact ? 'text-[11px]' : 'text-sm', monthMilestone ? 'text-accent' : 'text-ink hover:text-accent', monthFocused && 'bg-accent-soft')} data-id="element-190" data-mp="26xd72lopzip7">
                    {row.label}
                    {monthMilestone && <span className="bg-accent absolute -right-1 top-0 h-1.5 w-1.5 rounded-full" aria-hidden data-id="element-191" data-mp="3b7y3xzc6offt" />}
                  </button>} data-id="element-189" data-mp="2atr3tv21c4w7">
                {row.cells.map(cell => {
              if (cell.state === 'void') return <span key={cell.key} aria-hidden data-id="element-192" data-mp="3255e80ingbs3" />;
              const milestone = milestoneByCell.get(cell.key);
              const focused = focusedKey === cell.key;
              const accent = Boolean(milestone) || cell.state === 'current';
              const label = milestone ? `${cell.label} ${row.label}, milestone: ${milestone.title}` : `${cell.label} ${row.label}`;

              // numbers do not fit into 10px columns on a narrow screen — draw evenly spaced dots
              if (compact) {
                return <button key={cell.key} type="button" data-cell={cell.key} onClick={() => onSelectDay(cell)} aria-label={label} className="flex h-4 items-center justify-center focus-visible:outline focus-visible:outline-1 focus-visible:outline-accent" data-id="element-193" data-mp="29knoyiglpj9m">
                        <span aria-hidden className={cn('rounded-full', accent ? 'bg-accent' : cell.state === 'past' ? 'bg-ink' : 'bg-faint', focused && 'ring-accent ring-2')} style={{
                    width: accent ? 6 : 4,
                    height: accent ? 6 : 4,
                    boxShadow: accent ? '0 0 0 2px var(--paper)' : undefined
                  }} data-id="element-194" data-mp="1goeexrcbgawi" />
                      </button>;
              }
              return <button key={cell.key} type="button" data-cell={cell.key} title={milestone?.title} onClick={() => onSelectDay(cell)} aria-label={label} className={cn('relative py-0.5 text-center text-[11px] tabular-nums transition-colors duration-150 ease-swift focus-visible:outline focus-visible:outline-1 focus-visible:outline-accent', cell.state === 'past' && 'text-ink', cell.state === 'future' && 'text-muted hover:text-ink', cell.state === 'current' && 'text-accent font-semibold', milestone && 'text-accent font-semibold', focused && 'bg-accent-soft')} data-id="element-195" data-mp="2znm407ai9tvr">
                      {cell.label}
                      {accent && <span className="bg-accent absolute right-0 top-0 h-1 w-1 rounded-full" aria-hidden data-id="element-196" data-mp="vysqn8pnm09q" />}
                    </button>;
            })}
              </StrikeRow>;
        })}
        </div>
      </section>

      {counters.length > 0 && <section className="border-line border-t pt-4" aria-label="Still to come" data-id="element-197" data-mp="10xlt863ek6v4">
          <RemainingCounters items={counters} data-id="element-198" data-mp="1epk00a9n5qo4" />
        </section>}
    </div>;
}